import { constants } from './redux';
import {takeEvery, call, put, all} from "@redux-saga/core/effects";
import { push } from 'connected-react-router';
import requisitionListsApi from 'utils/resources/requisitionLists';
import { actions } from './redux';
import {clearSelectedRLTenders} from "../utils/hooks";
import {notifyError, notifyWarning} from "../../../utils/toastNotify";
import { REQUEST_CREATED_KEY, RL_SELECTED_KEY, SUPPLIERS_SELECTED_KEY } from '../requests/utils';


/**
 * Saga to create a new tender.
 *
 * @param {Object} payload - The payload object.
 * @yields {Generator} A Redux Saga generator.
 */
function* createTender ({ payload }) {
  try {
    const response = yield call(requisitionListsApi.createTender, payload);
    const id = response.data.data.id;
    yield put(actions.createTenderSuccess(response.data.data));
    yield put(push(`/requisition_lists/tenders/${id}`));
    yield call(clearSelectedRLTenders);
    // Clear created request and selected items from memory when tender is created
    localStorage.removeItem(REQUEST_CREATED_KEY);
    localStorage.removeItem(RL_SELECTED_KEY);
    localStorage.removeItem(SUPPLIERS_SELECTED_KEY);
  } catch (error) {
    console.error(error)
    yield call(notifyError, "Something went wrong", error.message);
  }
}

/**
 * Saga to create a new tender item.
 *
 * @param {Object} payload - The payload object.
 * @yields {Generator} A Redux Saga generator.
 */
function* createTenderItem ({ payload }) {
  try {
    const tenderId = payload.tender_id;
    yield call(requisitionListsApi.createTenderItem, payload);
    yield loadTenderTable({ payload: { tenderId } });
  } catch (error) {
    console.error(error)
    yield call(notifyError, "Something went wrong", error.message);
  }
}


/**
 * Saga to update a tender item.
 *
 * @param {Object} payload - The payload object.
 * @yields {Generator} A Redux Saga generator.
 */
function* updateTenderItem ({ payload: { id, tenderId, payload } }) {
  try {
    yield call(requisitionListsApi.updateTenderItem, id, payload);
    yield loadTenderTable({ payload: { tenderId } });
  } catch (error) {
    console.error(error)
    yield call(notifyError, "Something went wrong", error.message);
  }
}

/**
 * Saga to delete a tender item.
 *
 * @param {Object} payload - The payload object.
 * @yields {Generator} A Redux Saga generator.
 */
function* deleteTenderItem ({ payload: { tenderId, tenderItemId } }) {
  try {
    yield call(requisitionListsApi.deleteTenderItem, tenderItemId);
    yield loadTenderTable({ payload: { tenderId } });
  } catch (error) {
    console.error(error)
    yield call(notifyError, "Something went wrong", error.message);
  }
}

/**
 * Saga to bulk create tender items.
 *
 * @param {Object} payload - The payload object.
 * @yields {Generator} A Redux Saga generator.
 */
function* bulkCreateTenderItem ({ payload: { tenderId, data } }) {
  try {
    yield call(requisitionListsApi.bulkCreateTenderItems, { data });
    yield loadTenderTable({ payload: { tenderId } });
  } catch (error) {
    console.error(error)
    yield call(notifyError, "Something went wrong", error.message);
  }
}

/**
 * Saga to load the tender table.
 *
 * @param {Object} payload - The payload object.
 * @yields {Generator} A Redux Saga generator.
 */
export function* loadTenderTable({ payload: { tenderId } }){
  try {
    const response = yield call(requisitionListsApi.tenderTable, tenderId);
    yield put(actions.loadTenderTableSuccess(response.data));
  } catch (error) {
    console.error(error)
  }
}

/**
 * Saga to generate a purchase order.
 *
 * @param {Object} payload - The payload object.
 * @yields {Generator} A Redux Saga generator.
 */
export function* generatePO({ payload: { tenderId, data } }) {
  try {
    const response = yield call(requisitionListsApi.generatePO, { tenderId, data });
    yield put(actions.generatePOSuccess(response));
    yield* warnAboutMissingBudgetItems(response);
    yield call(loadTenderTable, { payload: { tenderId } });
  } catch (error) {
    console.error(error)
    yield call(notifyError, "Something went wrong", serverMessage(error));
  }

}

/**
 * Saga to add items to an existing purchase order.
 *
 * @param {Object} payload - The payload object.
 * @yields {Generator} A Redux Saga generator.
 */
export function* addToExistingPO({ payload: { tenderId, data } }) {
  try {
    const response = yield call(requisitionListsApi.addToExistingPO, { tenderId, data });
    console.log('addToExistingPO response:', response);
    yield put(actions.addToExistingPOSuccess(response));
    yield* warnAboutMissingBudgetItems(response);
    yield call(loadTenderTable, { payload: { tenderId } });
  } catch (error) {
    console.error(error)
    yield call(notifyError, "Something went wrong", serverMessage(error));
  }

}

/**
 * FIN-1274: the reason the backend gave for a failed order, or the request error without one.
 *
 * @param {Error} error - The request error.
 * @returns {string} The message to show.
 */
const serverMessage = (error) => error.response?.data?.error || error.message;

/**
 * FIN-1274: the lists whose budget item Finvisor does not have. Their order lines are
 * created without a budget item, and the order cannot go for approval until one is chosen.
 *
 * @param {Array<Object>} warnings - `{requisition_list, budget_item_id, budget_item}` per list.
 * @returns {string} The message naming every list.
 */
export const missingBudgetItemsMessage = (warnings) => {
  const lists = warnings
    .map((w) => `R/L ${w.requisition_list}: ${w.budget_item} (Shipvisor id ${w.budget_item_id})`)
    .join('; ');
  return `${lists}. Finvisor has no such budget item, so these order lines were created without one — `
    + 'choose a budget item on the order before sending it for approval.';
};

function* warnAboutMissingBudgetItems(response) {
  const warnings = response?.data?.warnings || [];
  if (warnings.length === 0) return;

  yield call(notifyWarning, 'Budget item not found', missingBudgetItemsMessage(warnings), 15000);
}

/**
 * Saga to remove a supplier.
 *
 * @param {Object} payload - The payload object.
 * @yields {Generator} A Redux Saga generator.
 */
function* removeSupplier({ payload: { tenderId, supplierId, quotationIds } }) {
  try {
    if (quotationIds && quotationIds.length > 0) {
      yield all(quotationIds.map(quotationId =>
        call(requisitionListsApi.removeSupplier, { tenderId, supplierId, quotationId })
      ));
    } else {
      yield call(requisitionListsApi.removeSupplier, { tenderId, supplierId });
    }
    yield loadTenderTable({ payload: { tenderId } });
  } catch (error) {
    console.error(error)
    yield call(notifyError, "Something went wrong", error.message);
  }

}

/**
 * Returns an array of sagas to be run.
 *
 * @returns {Array} An array of sagas.
 */
export default function getFeatureSagas() {
  return [
    takeEvery([
      constants.CREATE_TENDER_REQUEST
    ], createTender),
    takeEvery([
      constants.CREATE_TENDER_ITEM_REQUEST
    ], createTenderItem),
    takeEvery([
      constants.BULK_CREATE_TENDER_ITEM_REQUEST
    ], bulkCreateTenderItem),
    takeEvery([
      constants.UPDATE_TENDER_ITEM_REQUEST
    ], updateTenderItem),
    takeEvery([
      constants.DELETE_TENDER_ITEM_REQUEST
    ], deleteTenderItem),
    takeEvery([
      constants.LOAD_TENDER_TABLE_REQUEST
    ], loadTenderTable),
    takeEvery([
      constants.GENERATE_PO_REQUEST
    ], generatePO),
    takeEvery([
      constants.ADD_TO_EXISTING_PO_REQUEST
    ], addToExistingPO),
    takeEvery([
      constants.REMOVE_SUPPLIER_REQUEST
    ], removeSupplier),
  ];
}
